import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    MdAccountTree,
    MdCloudSync,
    MdCode,
    MdStorefront,
    MdUpload,
} from 'react-icons/md';
import CatalogSources from './CatalogSources';
import RetailerCategoryMappings from './RetailerCategoryMappings';
import ImportsPage from '../Imports/ImportsPage';
import RetailerInstallations from '../Integrations/RetailerInstallations';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import TabBar from '../../../shared/layout/tabs/TabBar';
import TabButton from '../../../shared/layout/tabs/TabButton';
import StyledCard from '../../../shared/layout/cards/StyledCard';

const Shell = styled.main`
    min-width: 0;
`;

const HeaderCopy = styled.div`
    min-width: 0;
`;

const Intro = styled.p`
    margin: 0.55rem 0 0;
    color: ${({ theme }) => theme.colors.textSecondary || theme.colors.textMuted || '#66727d'};
    font-size: 0.95rem;
    line-height: 1.5;
    max-width: 850px;
`;

const WorkspaceTabs = styled(TabBar)`
    margin-top: 0;
    border-bottom-color: ${({ theme }) => theme.colors.border || '#e5e7eb'};

    @media (max-width: 900px) {
        flex-wrap: wrap;
        gap: 0.35rem;
    }
`;

const Tab = styled(TabButton)`
    min-width: 0;
    white-space: nowrap;
    border-radius: 7px 7px 0 0;

    @media (max-width: 900px) {
        flex-basis: calc(50% - 0.5rem);
    }

    @media (max-width: 560px) {
        flex-basis: 100%;
        justify-content: flex-start;
        text-align: left;
    }
`;

const View = styled.section`
    min-width: 0;
    margin-top: 1rem;
`;

const ViewDescription = styled.p`
    margin: 0 0 1rem;
    color: ${({ theme }) => theme.colors.textSecondary || theme.colors.textMuted || '#66727d'};
    line-height: 1.5;
`;

const ScraperCard = styled(StyledCard)`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin: 0;

    h2 {
        margin: 0 0 0.45rem;
        color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
        font-size: 1.1rem;
    }

    p {
        margin: 0;
        color: ${({ theme }) => theme.colors.textSecondary || theme.colors.textMuted || '#66727d'};
        line-height: 1.55;
    }

    a {
        display: inline-flex;
        margin-top: 0.85rem;
        color: ${({ theme }) => theme.colors.brand || '#701a82'};
        font-weight: 600;
        text-decoration: none;
    }
`;

const ScraperIcon = styled.span`
    display: grid;
    place-items: center;
    flex: 0 0 2.5rem;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.65rem;
    background: ${({ theme }) => theme.colors.brandLighter || 'rgba(130, 22, 146, 0.1)'};
    color: ${({ theme }) => theme.colors.brand || '#701a82'};
    font-size: 1.35rem;
`;

export default function CatalogImportWorkspace() {
    const [params, setParams] = useSearchParams();
    const { t } = useTranslation();
    const tr = (key, fallback) => t(`CatalogWorkspace.${key}`, { defaultValue: fallback });

    const tabs = [
        { key: 'sources', label: tr('sources', 'Moji izvori / URL'), description: tr('sourceHelp', 'Dodajte URL kataloga, pregledajte uzorak i potvrdite mapiranje prije prvog synca.'), icon: <MdCloudSync /> },
        { key: 'upload', label: tr('upload', 'Učitaj CSV'), description: tr('uploadHelp', 'Uvezite CSV datoteku jednokratno ili je koristite za početno punjenje kataloga.'), icon: <MdUpload /> },
        { key: 'stores', label: tr('stores', 'Integracije trgovina'), description: tr('storesHelp', 'Upravljajte povezanim trgovinama i njihovim sigurnim integracijama.'), icon: <MdStorefront /> },
        { key: 'scraper', label: tr('scraper', 'Custom scraper'), icon: <MdCode /> },
        { key: 'categories', label: tr('categories', 'Mapiranje kategorija'), description: tr('categoriesHelp', 'Pregledajte i potvrdite kako su kategorije iz feeda povezane s DigiWarranty kategorijama.'), icon: <MdAccountTree /> },
    ];
    const requestedView = params.get('view') || 'sources';
    const view = tabs.some(({ key }) => key === requestedView) ? requestedView : 'sources';

    const selectView = (key) => {
        const next = new URLSearchParams(params);
        next.set('view', key);
        if (key !== 'categories') next.delete('source');
        setParams(next);
    };

    return (
        <Shell>
            <PageHeader>
                <HeaderCopy>
                    <PageTitle>{tr('title', 'Uvoz i sinkronizacija kataloga')}</PageTitle>
                    <Intro>{tr('intro', 'Povežite izvor kataloga, uvezite datoteku ili upravljajte integracijama trgovine.')}</Intro>
                </HeaderCopy>
            </PageHeader>

            <PageContent>
                <WorkspaceTabs role="group" aria-label={tr('navigation', 'Način uvoza i postavke')}>
                    {tabs.map(({ key, label, icon }) => (
                        <Tab
                            key={key}
                            type="button"
                            aria-pressed={view === key}
                            active={view === key}
                            onClick={() => selectView(key)}
                            icon={icon}
                        >
                            {label}
                        </Tab>
                    ))}
                </WorkspaceTabs>

                <View>
                    {tabs.find((tab) => tab.key === view)?.description && (
                        <ViewDescription>{tabs.find((tab) => tab.key === view).description}</ViewDescription>
                    )}
                    {view === 'sources' && <CatalogSources />}
                    {view === 'upload' && <ImportsPage />}
                    {view === 'stores' && <RetailerInstallations />}
                    {view === 'categories' && <RetailerCategoryMappings />}
                    {view === 'scraper' && (
                        <ScraperCard>
                            <ScraperIcon><MdCode /></ScraperIcon>
                            <div>
                                <h2>{tr('scraperTitle', 'Izvor kojim upravlja custom scraper')}</h2>
                                <p>{tr('scraperHelp', 'Scraper se konfigurira za konkretnu trgovinu. Ne unosite običnu web-stranicu kao CSV/XML/JSON feed. Postojeći povezani scraperi prikazuju se među vašim izvorima; dodavanje novog zahtijeva administratorsku konfiguraciju.')}</p>
                                <Link to="?view=sources">{tr('sources', 'Moji izvori / URL')}</Link>
                            </div>
                        </ScraperCard>
                    )}
                </View>
            </PageContent>
        </Shell>
    );
}
